import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import {
  getAvailableSwitchTargets,
  getCurrentRuntimeTarget,
  RuntimeTarget,
  switchRuntimeTarget,
} from '../../config/runtime-target';
import {
  fetchEnvironmentSettings,
  readCachedEnvironmentSettings,
  testingBannerShown,
  type BrandEnvironmentSettings,
} from '../../config/environmentSettings';
import { getBrand } from '../constants/brands';

/** Full-width within AppLayoutRoot — avoid 100vw (wider than layout on mobile; clips right gutters). */
const BannerShell = styled.div`
  ${tw`w-full max-w-full min-w-0 shrink-0 overflow-hidden box-border`}
`;

const Banner = styled.div`
  ${tw`
    box-border
    bg-amber-500
    text-black
    text-center
    text-xs
    sm:text-sm
    font-semibold
    leading-snug
    py-2
    break-words
  `}
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: max(0.75rem, env(safe-area-inset-left, 0px));
  padding-right: max(0.75rem, env(safe-area-inset-right, 0px));
`;

/**
 * Spec 027: the brand's testing banner switch, read from production. Starts on
 * the last answer for this brand (else shown) and asks again on every load.
 */
function useTestingBannerShown(isStaging: boolean): boolean {
  const brandSite = getBrand().brandSite;
  const [settings, setSettings] = useState<BrandEnvironmentSettings | null>(() =>
    isStaging ? readCachedEnvironmentSettings(brandSite) : null,
  );
  useEffect(() => {
    if (!isStaging) return undefined;
    let active = true;
    fetchEnvironmentSettings({ brandSite }).then(answer => {
      if (active && answer) setSettings(answer);
    });
    return () => {
      active = false;
    };
  }, [isStaging, brandSite]);
  return testingBannerShown(isStaging, settings);
}

export function TestModeBanner() {
  const bannerRef = useRef<HTMLDivElement>(null);
  const shown = useTestingBannerShown(getCurrentRuntimeTarget() === 'staging');

  useEffect(() => {
    if (!shown) return undefined;
    const el = bannerRef.current;
    if (!el) return undefined;

    const applyHeight = () => {
      document.documentElement.style.setProperty('--staging-banner-height', `${el.offsetHeight}px`);
    };
    applyHeight();

    const observer = new ResizeObserver(applyHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--staging-banner-height');
    };
  }, [shown]);

  if (!shown) return null;
  return (
    <BannerShell ref={bannerRef}>
      <Banner>Test mode — staging environment. Bookings here are not real.</Banner>
    </BannerShell>
  );
}

const Section = styled.div`
  ${tw`w-full max-w-full min-w-0 mb-6 p-4 border border-gray-200 rounded-lg box-border`}
`;

const Row = styled.button<{ $active?: boolean }>`
  ${tw`w-full max-w-full min-w-0 text-left px-3 py-2 rounded mb-2 border box-border`}
  ${({ $active }) =>
    $active ? tw`bg-gray-100 text-gray-400 cursor-not-allowed` : tw`hover:bg-gray-50`}
`;

const labelFor: Record<RuntimeTarget, string> = {
  development: 'Development',
  staging: 'Staging',
  production: 'Production',
};

export function EnvironmentSwitchSection() {
  const current = getCurrentRuntimeTarget();
  const options = getAvailableSwitchTargets();

  return (
    <Section>
      <Label>Environment</Label>
      {options.map(option => (
        <Row
          key={option}
          type="button"
          disabled={option === current}
          $active={option === current}
          onClick={() => {
            if (option !== current) {
              void switchRuntimeTarget(option);
            }
          }}
        >
          {labelFor[option]}
          {option === current ? ' (current)' : ''}
        </Row>
      ))}
    </Section>
  );
}

const Label = styled.p`
  ${tw`text-sm text-gray-600 mb-2 font-medium`}
`;
